import { makeAutoObservable } from "mobx";
import { CandidateStore } from "./candidate.store";
import type {
  AutomationConfig,
  Application,
} from "@smartsuite/automation-engine-api";
import {
  INPUT_APPLICATION_ID,
  Integrations,
  isSmartSuiteIntegration,
} from "../../utils/constants";
import { findInputAndPick, pickValueConfigString } from "../../utils/inputs";
import { featureFlagsStore } from "../feature-flags.store";

export class AutomationsStore {
  candidate: CandidateStore = new CandidateStore();

  list: AutomationConfig[] = [];
  scrollListState = 0;
  pageToken: string = "";
  pageSize: number = 0;
  automationsCallResolved = false;
  loadingDynamicDescription = false;
  isRefreshingList = false;
  loadingDynamicDescriptionProcess: Array<{
    type: "trigger" | "action";
    actionInstanceId?: number;
  }> = [];

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Single gate for the automations-per-solution cap, driven by the
   * `max_automations_per_solution` UCES flag. The list is loaded page by page
   * until `nextPageToken` is empty (containers/App), so `list.length` is the
   * solution's full count once loading resolves.
   */
  get isLimitReached(): boolean {
    return (
      this.list.length >= featureFlagsStore.limits.maxAutomationsPerSolution
    );
  }

  setAutomationsCallResolved(isLoading: boolean): void {
    this.automationsCallResolved = isLoading;
  }

  setScrollListState(state: number): void {
    this.scrollListState = state;
  }

  // Set of functions to check if all request for dynamic descriptions were finished (except current opened action)
  addToLoadingDynamicDescriptionProcess(
    type: "trigger" | "action",
    actionInstanceId?: number,
  ): void {
    const itemIndex = this.loadingDynamicDescriptionProcess.findIndex(
      (item) =>
        item.type === type && item.actionInstanceId === actionInstanceId,
    );
    if (itemIndex === -1) {
      this.loadingDynamicDescriptionProcess.push({
        type,
        actionInstanceId,
      });
    }
  }

  isAllDynamicTriggerDescriptionsLoading(): boolean {
    const item = this.loadingDynamicDescriptionProcess.find(
      (item) => item.type === "trigger",
    );
    return item !== undefined;
  }

  removeFromLoadingDynamicDescriptionProcess(
    type: "trigger" | "action",
    actionInstanceId?: number,
  ): void {
    const itemIndex = this.loadingDynamicDescriptionProcess.findIndex(
      (item) =>
        item.type === type && item.actionInstanceId === actionInstanceId,
    );
    if (itemIndex !== -1) {
      this.loadingDynamicDescriptionProcess.splice(itemIndex, 1);
    }
  }

  isAllDynamicDescriptionsLoaded(): boolean {
    return this.loadingDynamicDescriptionProcess.length === 0;
  }

  setLoadingDynamicDescription(value: boolean): void {
    this.loadingDynamicDescription = value;
  }

  setRefreshingList(value: boolean): void {
    this.isRefreshingList = value;
  }

  appendPage(nextList: AutomationConfig[], pageToken: string): void {
    this.list = [
      ...this.list,
      ...nextList.filter(
        (a) =>
          !this.list.some(
            (curAutomation) => curAutomation.automationId === a.automationId,
          ),
      ),
    ];
    this.pageToken = pageToken;
  }

  // check if it have been needed?
  replaceList(list: AutomationConfig[], pageToken: string): void {
    this.list = list;
    this.pageToken = pageToken;
  }

  mergeItem(automation: AutomationConfig): void {
    if (this.list.some((a) => a.automationId === automation.automationId)) {
      this.updateItem(automation);
    } else {
      this.addItem(automation);
    }
  }

  addItem(automation: AutomationConfig): void {
    this.list = [automation, ...this.list];
  }

  updateItem(automation: AutomationConfig): void {
    this.list = this.list.map((a) =>
      a.automationId === automation.automationId ? automation : a,
    );
  }

  deleteItem(automationId: string): void {
    this.list = this.list.filter((a) => a.automationId !== automationId);
  }

  purgeList(): void {
    this.list = [];
    this.pageToken = "";
  }

  getAutomationsWithValidTrigger(
    applications: Application[] = [],
  ): AutomationConfig[] {
    return this.list.filter((item) => {
      const integrationId = String(
        item.trigger?.triggerReference?.integrationId,
      );
      if (
        isSmartSuiteIntegration(integrationId) &&
        integrationId !== Integrations.webhooks
      ) {
        if (
          item.trigger?.inputs &&
          item.trigger?.inputs.length > 0 &&
          applications.length > 0
        ) {
          const applicationId = findInputAndPick(
            item.trigger.inputs,
            INPUT_APPLICATION_ID,
            pickValueConfigString,
          );
          return applications.some((app) => app.id === applicationId);
        }
        return false;
      }
      return true;
    });
  }
}
